import { useEffect, useMemo, useRef, useState, type FC } from 'react'
import { useTranslation } from 'react-i18next'
import type { IconBaseProps } from 'react-icons'
import {
  MdSearch as MdSearchRaw,
  MdChevronRight as MdChevronRightRaw,
  MdChevronLeft as MdChevronLeftRaw,
  MdEdit as MdEditRaw,
  MdGTranslate as MdGTranslateRaw,
  MdShield as MdShieldRaw,
  MdPerson as MdPersonRaw,
  MdHeadsetMic as MdHeadsetMicRaw,
} from 'react-icons/md'
import './Settings.scss'
import SearchInput from '../../components/SearchInput/SearchInput'

/**
 * FIX TS2786 для react-icons (на строгих TS конфигурациях бывает)
 */
const MdSearch = MdSearchRaw as unknown as FC<IconBaseProps>
const MdChevronRight = MdChevronRightRaw as unknown as FC<IconBaseProps>
const MdChevronLeft = MdChevronLeftRaw as unknown as FC<IconBaseProps>
const MdEdit = MdEditRaw as unknown as FC<IconBaseProps>
const MdGTranslate = MdGTranslateRaw as unknown as FC<IconBaseProps>
const MdShield = MdShieldRaw as unknown as FC<IconBaseProps>
const MdPerson = MdPersonRaw as unknown as FC<IconBaseProps>
const MdHeadsetMic = MdHeadsetMicRaw as unknown as FC<IconBaseProps>

type ThemeMode = 'dark' | 'light' | 'system'
type Lang = 'en' | 'ru' | 'fr'
type InfoKey = 'confidential' | 'account' | 'contacts'

export default function Settings() {
  const { i18n } = useTranslation()

  const [searchValue, setSearchValue] = useState('')

  // dropdowns
  const [themeOpen, setThemeOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)

  const [theme, setTheme] = useState<ThemeMode>('dark')
  const [lang, setLang] = useState<Lang>(
    (((i18n.language || 'en').split('-')[0] as Lang) || 'en') as Lang,
  )

  // ✅ general info panel: по дефолту НИЧЕГО не открыто
  const [activeInfo, setActiveInfo] = useState<InfoKey | null>(null)
  const [detailsTop, setDetailsTop] = useState(0)

  // anchors / popovers
  const themeAnchorRef = useRef<HTMLButtonElement | null>(null)
  const themePopoverRef = useRef<HTMLDivElement | null>(null)
  const langAnchorRef = useRef<HTMLButtonElement | null>(null)
  const langPopoverRef = useRef<HTMLDivElement | null>(null)

  // left column + info row refs
  const leftColRef = useRef<HTMLDivElement | null>(null)
  const confRef = useRef<HTMLButtonElement | null>(null)
  const accRef = useRef<HTMLButtonElement | null>(null)
  const contRef = useRef<HTMLButtonElement | null>(null)

  const languageLabel = useMemo(() => {
    if (lang === 'ru') return 'Russian'
    if (lang === 'fr') return 'French'
    return 'English'
  }, [lang])

  const themeLabel = useMemo(() => {
    if (theme === 'dark') return 'Dark'
    if (theme === 'light') return 'Light'
    return 'Follow to device'
  }, [theme])

  const infoTitle = useMemo(() => {
    if (activeInfo === 'confidential') return 'Confidentiality Agreement'
    if (activeInfo === 'account') return 'Account Info'
    if (activeInfo === 'contacts') return 'Contacts'
    return ''
  }, [activeInfo])

  // закрытие popover кликом вне
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node

      if (themeOpen) {
        const a = themeAnchorRef.current
        const p = themePopoverRef.current
        if (a && a.contains(t)) return
        if (p && p.contains(t)) return
        setThemeOpen(false)
      }

      if (langOpen) {
        const a = langAnchorRef.current
        const p = langPopoverRef.current
        if (a && a.contains(t)) return
        if (p && p.contains(t)) return
        setLangOpen(false)
      }
    }

    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [themeOpen, langOpen])

  // ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      setThemeOpen(false)
      setLangOpen(false)
      setActiveInfo(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const applyTheme = (next: ThemeMode) => {
    setTheme(next)
    setThemeOpen(false)
    // TODO: подключи реальную тему (redux/context)
  }

  const applyLang = (next: Lang) => {
    setLang(next)
    setLangOpen(false)
    // TODO: i18n.changeLanguage(next)
  }

  // выравнивание правого контента по выбранному пункту General Info
  const calcDetailsTop = () => {
    if (!activeInfo) {
      setDetailsTop(0)
      return
    }
    const left = leftColRef.current
    if (!left) return

    const leftRect = left.getBoundingClientRect()

    const getRef = () => {
      if (activeInfo === 'confidential') return confRef.current
      if (activeInfo === 'account') return accRef.current
      return contRef.current
    }

    const row = getRef()
    if (!row) return

    const rowRect = row.getBoundingClientRect()
    const top = Math.max(0, rowRect.top - leftRect.top - 6)
    setDetailsTop(top)
  }

  useEffect(() => {
    calcDetailsTop()
    window.addEventListener('resize', calcDetailsTop)
    return () => window.removeEventListener('resize', calcDetailsTop)
  }, [activeInfo])

  useEffect(() => {
    calcDetailsTop()
  }, [themeOpen, langOpen])

  const Row = (props: {
    labelTop?: string
    value: string
    mobileTitle?: string
    mobileRight?: string
    active?: boolean
    onClick?: () => void
    buttonRef?: any
    chevronAccent?: boolean
    IconMobile?: FC<IconBaseProps> // ✅ иконка ТОЛЬКО для mobile
  }) => {
    const {
      labelTop,
      value,
      mobileTitle,
      mobileRight,
      active,
      onClick,
      buttonRef,
      chevronAccent,
      IconMobile = MdSearch,
    } = props

    return (
      <div className="stRowWrap">
        {labelTop ? (
          <div className="stRowLabel">{labelTop}</div>
        ) : (
          <div className="stRowLabel stRowLabel--empty" />
        )}

        <button
          ref={buttonRef}
          type="button"
          className={`stRow ${active ? 'isActive' : ''} ${
            chevronAccent ? 'isChevronAccent' : ''
          }`}
          onClick={onClick}
        >
          <div className="stRowLeft">
            {/* ✅ Desktop: всегда MdSearch */}
            <span className="stRowIcon stRowIconDesktop" aria-hidden="true">
              <MdSearch size={16} />
            </span>

            {/* ✅ Mobile: те иконки, которые ты ставил */}
            <span className="stRowIcon stRowIconMobile" aria-hidden="true">
              <IconMobile size={18} />
            </span>

            {/* Desktop: как было (value) */}
            <span className="stRowTextDesktop">{value}</span>

            {/* Mobile: как на фотке (левый title) */}
            <span className="stRowTextMobile">{mobileTitle ?? value}</span>
          </div>

          <div className="stRowRight">
            {mobileRight ? (
              <span className="stRowRightValue" aria-hidden="true">
                {mobileRight}
              </span>
            ) : null}

            <span className="stRowChevron" aria-hidden="true">
              <MdChevronRight size={18} />
            </span>
          </div>
        </button>
      </div>
    )
  }

  const ConfidentialText = () => (
    <div style={{ paddingBottom: '100px' }} className="stText">
      <div className="stTextPart">
        <div className="stTextH">Part 1</div>
        <p>
          У авторежимі теми застосунку “темна” і “світла” теми автоматично застосовуються
          відповідно до Вашого часового поясу.
        </p>
        <p>
          У авторежимі теми застосунку “темна” і “світла” теми автоматично застосовуються
          відповідно до Вашого часового поясу.
        </p>
      </div>

      <div className="stTextPart">
        <div className="stTextH">Part 2</div>
        <p>
          У авторежимі теми застосунку “темна” і “світла” теми автоматично застосовуються
          відповідно до Вашого часового поясу.
        </p>
        <p>
          У авторежимі теми застосунку “темна” і “світла” теми автоматично застосовуються
          відповідно до Вашого часового поясу.
        </p>
      </div>

      <div className="stTextPart">
        <div className="stTextH">Part 3</div>
        <p>
          У авторежимі теми застосунку “темна” і “світла” теми автоматично застосовуються
          відповідно до Вашого часового поясу.
        </p>
        <p>
          У авторежимі теми застосунку “темна” і “світла” теми автоматично застосовуються
          відповідно до Вашого часового поясу.
        </p>
      </div>
    </div>
  )

  const ContactsPanel = () => {
    const [dept, setDept] = useState<'marketing' | 'tech'>('marketing')
    const [msg, setMsg] = useState('')

    return (
      <div className="stContactCard">
        <div className="stContactTitle">Send to</div>

        <div className="stRadioList">
          <label className="stRadioLine">
            <span className="stRadioText">Marketing Departement</span>
            <input
              type="radio"
              name="dept"
              checked={dept === 'marketing'}
              onChange={() => setDept('marketing')}
            />
            <span className="stRadioUi" />
          </label>

          <label className="stRadioLine">
            <span className="stRadioText">Technical Departement</span>
            <input
              type="radio"
              name="dept"
              checked={dept === 'tech'}
              onChange={() => setDept('tech')}
            />
            <span className="stRadioUi" />
          </label>
        </div>

        <textarea
          className="stTextarea"
          placeholder="Write your message here here"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
        />

        <button type="button" className="stSendBtn">
          Send message
        </button>
      </div>
    )
  }

  // ✅ helper: toggle info panel
  const toggleInfo = (key: InfoKey) => {
    setThemeOpen(false)
    setLangOpen(false)
    setActiveInfo((prev) => (prev === key ? null : key))
  }

  return (
    <div className="settingsPage">
      <div className="settingsBg" aria-hidden="true" />

      <div className="settingsInner">
        <div className="settingsTop">
          <SearchInput />
        </div>

        <h1 style={{ marginTop: '105px' }} className="settingsTitle">
          Settings
        </h1>

        <div className="settingsGrid">
          {/* LEFT */}
          <div className="settingsLeft" ref={leftColRef}>
            <div className="settingsBlock">
              <h2 className="settingsH2">Theme</h2>

              <div className="stRowSlot">
                <Row
                  labelTop="CHANGE THEME"
                  value={themeLabel}
                  mobileTitle="Change Theme"
                  mobileRight={themeLabel}
                  active={themeOpen}
                  onClick={() => {
                    setLangOpen(false)
                    setThemeOpen((v) => !v)
                  }}
                  chevronAccent
                  buttonRef={themeAnchorRef}
                  IconMobile={MdEdit} // ✅ mobile
                />

                {themeOpen && (
                  <div ref={themePopoverRef} className="stPopover" role="dialog">
                    <button
                      type="button"
                      className="stPopRow"
                      onClick={() => applyTheme('dark')}
                    >
                      <span className="stPopText">Dark</span>
                      <span className={`stRadioDot ${theme === 'dark' ? 'isOn' : ''}`} />
                    </button>

                    <button
                      type="button"
                      className="stPopRow"
                      onClick={() => applyTheme('light')}
                    >
                      <span className="stPopText">Light</span>
                      <span className={`stRadioDot ${theme === 'light' ? 'isOn' : ''}`} />
                    </button>

                    <button
                      type="button"
                      className="stPopRow"
                      onClick={() => applyTheme('system')}
                    >
                      <span className="stPopText">Follow to device</span>
                      <span
                        className={`stRadioDot ${theme === 'system' ? 'isOn' : ''}`}
                      />
                    </button>
                  </div>
                )}
              </div>

              <div className="stGap" />

              <div className="stRowSlot">
                <Row
                  labelTop="LANGUAGE"
                  value={languageLabel}
                  mobileTitle="Language"
                  mobileRight={languageLabel}
                  active={langOpen}
                  onClick={() => {
                    setThemeOpen(false)
                    setLangOpen((v) => !v)
                  }}
                  chevronAccent
                  buttonRef={langAnchorRef}
                  IconMobile={MdGTranslate} // ✅ mobile
                />

                {langOpen && (
                  <div ref={langPopoverRef} className="stPopover" role="dialog">
                    <button
                      type="button"
                      className="stPopRow"
                      onClick={() => applyLang('en')}
                    >
                      <span className="stPopText">English</span>
                      <span className={`stRadioDot ${lang === 'en' ? 'isOn' : ''}`} />
                    </button>

                    <button
                      type="button"
                      className="stPopRow"
                      onClick={() => applyLang('ru')}
                    >
                      <span className="stPopText">Russian</span>
                      <span className={`stRadioDot ${lang === 'ru' ? 'isOn' : ''}`} />
                    </button>

                    <button
                      type="button"
                      className="stPopRow"
                      onClick={() => applyLang('fr')}
                    >
                      <span className="stPopText">French</span>
                      <span className={`stRadioDot ${lang === 'fr' ? 'isOn' : ''}`} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="settingsBlock settingsBlock--spaced">
              <h2 className="settingsH2">General Info</h2>

              <Row
                value="Confidentiality Agreement"
                onClick={() => toggleInfo('confidential')}
                buttonRef={confRef}
                chevronAccent={activeInfo === 'confidential'}
                IconMobile={MdShield} // ✅ mobile
              />

              <Row
                value="Account Info"
                onClick={() => toggleInfo('account')}
                buttonRef={accRef}
                chevronAccent={activeInfo === 'account'}
                IconMobile={MdPerson} // ✅ mobile
              />

              <Row
                value="Contacts"
                onClick={() => toggleInfo('contacts')}
                buttonRef={contRef}
                chevronAccent={activeInfo === 'contacts'}
                IconMobile={MdHeadsetMic} // ✅ mobile
              />
            </div>

            <button type="button" className="stLogout">
              Logout
            </button>
          </div>

          {/* RIGHT (desktop) */}
          <div className="settingsRight">
            {activeInfo && (
              <div
                className="stRightFloat"
                style={{ transform: `translateY(${detailsTop}px)` }}
              >
                {activeInfo === 'confidential' && <ConfidentialText />}

                {activeInfo === 'account' && (
                  <div className="stText">
                    <div className="stTextPart">
                      <div className="stTextH">Account info</div>
                      <p>Тут будет инфа аккаунта пока нету</p>
                    </div>
                  </div>
                )}

                {activeInfo === 'contacts' && <ContactsPanel />}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ Mobile overlay для General Info (назад есть для Confidential/Account/Contacts) */}
      {activeInfo && (
        <div className="stMobileOverlay" role="dialog" aria-modal="true">
          <div className="stMobileHeader">
            <button
              type="button"
              className="stMobileBack"
              onClick={() => setActiveInfo(null)}
              aria-label="Back"
            >
              <MdChevronLeft size={22} />
            </button>

            <div className="stMobileHeaderTitle">{infoTitle}</div>
          </div>

          <div className="stMobileBody">
            {activeInfo === 'confidential' && <ConfidentialText />}

            {activeInfo === 'account' && (
              <div className="stText">
                <div className="stTextPart">
                  <div className="stTextH">Account info</div>
                  <p>Тут будет инфа аккаунта пока нету</p>
                </div>
              </div>
            )}

            {activeInfo === 'contacts' && <ContactsPanel />}
          </div>
        </div>
      )}
    </div>
  )
}
